import React from "react";
import { ChevronDown } from "lucide-react";
import StoreDealCard from "./StoreDealCard";
import StoreProductSkeleton from "./StoreProductSkeleton";

const FILTER_CATEGORIES = ["Computing", "Electronics", "Phones & Tablets", "Home & Office"];

const PRICE_RANGES = [
  "Under ₵500",
  "₵500 - ₵2,000",
  "₵2,000 - ₵10,000",
  "₵10,000 & above",
];

const GRID_SLOTS = {
  mobile: 4,
  tablet: 6,
  desktop: 8,
};

const FilterSidebar = ({ compact = false }) => (
  <aside
    className={`shrink-0 bg-white border border-gray-200 rounded-sm ${
      compact ? "w-[120px]" : "w-[148px] xl:w-[168px]"
    }`}
  >
    <div className="px-2.5 py-2 border-b border-gray-100">
      <p className="text-[9px] font-bold tracking-wide text-gray-500 uppercase">Category</p>
      <ul className="mt-1.5 space-y-1">
        {FILTER_CATEGORIES.map((item) => (
          <li key={item}>
            <span className="block text-[10px] text-gray-700 hover:text-[#FF6C6C] cursor-default">
              {item}
            </span>
          </li>
        ))}
      </ul>
    </div>

    <div className="px-2.5 py-2">
      <div className="flex items-center justify-between">
        <p className="text-[9px] font-bold tracking-wide text-gray-500 uppercase">Price (₵)</p>
        <span className="text-[9px] font-semibold text-[#FF6C6C]">Apply</span>
      </div>
      <div className="relative h-1.5 mt-3 mb-2 bg-gray-200 rounded-full">
        <div className="absolute inset-y-0 left-[10%] right-[30%] bg-[#FF6C6C] rounded-full" />
        <span className="absolute top-1/2 left-[10%] w-2.5 h-2.5 -translate-y-1/2 bg-white border-2 border-[#FF6C6C] rounded-full" />
        <span className="absolute top-1/2 right-[30%] w-2.5 h-2.5 -translate-y-1/2 bg-white border-2 border-[#FF6C6C] rounded-full" />
      </div>
      <div className="grid grid-cols-2 gap-1">
        <span className="px-1 py-0.5 text-[9px] text-center text-gray-600 border border-gray-200 rounded">
          189
        </span>
        <span className="px-1 py-0.5 text-[9px] text-center text-gray-600 border border-gray-200 rounded">
          75660
        </span>
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        <p className="text-[9px] font-bold tracking-wide text-gray-500 uppercase">Price range</p>
        <div className="mt-1.5 space-y-1.5">
          {PRICE_RANGES.map((range, index) => (
            <div key={range} className="flex items-start gap-1.5">
              <span
                className={`mt-0.5 w-2.5 h-2.5 shrink-0 rounded-full border ${
                  index === 1
                    ? "border-[#FF6C6C] bg-[#FF6C6C]"
                    : "border-gray-300 bg-white"
                }`}
              />
              <span className="text-[9px] leading-snug text-gray-600">{range}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  </aside>
);

const StoreCatalogPreview = ({
  brandName,
  deals = [],
  previewMode = "desktop",
  isPublished = true,
}) => {
  const isMobile = previewMode === "mobile";
  const isTablet = previewMode === "tablet";
  const isCompact = isMobile || isTablet;

  const productCount = deals.length;
  const maxSlots = GRID_SLOTS[previewMode] || GRID_SLOTS.desktop;
  const displayDeals = deals.slice(0, maxSlots);
  const skeletonCount = Math.max(0, maxSlots - displayDeals.length);

  const gridClass = isMobile
    ? "grid-cols-2"
    : isTablet
    ? "grid-cols-3"
    : "grid-cols-2 md:grid-cols-3 xl:grid-cols-4";

  return (
    <section className="px-3 pb-6 bg-[#F1F1F2]">
      <div className="max-w-6xl mx-auto">
        <div className="flex gap-2 sm:gap-3">
          {isMobile ? null : <FilterSidebar compact={isTablet} />}

          <div className="flex-1 min-w-0 bg-white border border-gray-200 rounded-sm">
            <div className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 border-b border-gray-100">
              <h2 className="text-[11px] font-bold text-gray-900 sm:text-xs">
                {brandName} Official Store
                <span className="font-normal text-gray-600">
                  {" "}
                  ({productCount > 0 ? productCount : "—"} product
                  {productCount === 1 ? "" : "s"} found)
                </span>
              </h2>
              <div className="flex items-center gap-1 text-[10px] text-gray-600">
                <span>Sort by:</span>
                <span className="inline-flex items-center gap-0.5 font-semibold text-gray-800">
                  Popularity
                  <ChevronDown className="w-3 h-3" />
                </span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-gray-100">
              <span className="inline-flex items-center gap-1 px-2 py-1 text-[9px] font-medium text-gray-700 border border-gray-200 rounded-sm bg-white">
                Price
                <ChevronDown className="w-3 h-3" />
              </span>
              {!isPublished ? (
                <span className="ml-auto px-2 py-0.5 text-[9px] font-semibold text-amber-700 bg-amber-50 border border-amber-100 rounded-full">
                  Preview
                </span>
              ) : null}
            </div>

            <div className={`grid gap-2 p-2 sm:gap-2.5 sm:p-3 ${gridClass}`}>
              {displayDeals.map((deal) => (
                <StoreDealCard
                  key={deal.id || deal.slug}
                  deal={deal}
                  storeName={brandName}
                  compact={isCompact}
                />
              ))}
              {Array.from({ length: skeletonCount }, (_, index) => (
                <StoreProductSkeleton key={`skeleton-${index}`} compact={isCompact} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default StoreCatalogPreview;
